import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import clinicalNoteService from './clinicalNoteService.js';
import './ClinicalNoteTreatmentCompact.css';

/**
 * Delay between the clinician's final edit and autosave.
 */
const AUTOSAVE_DELAY_MS = 900;

/**
 * Resolve ClinicalNoteMain.entityVersion from a note response.
 */
function resolveEntityVersion(response) {
  if (!response) {
    return null;
  }

  return (
    response.entityVersion ??
    response.note?.entityVersion ??
    response.clinicalNote?.entityVersion ??
    null
  );
}

/**
 * Convert nullable backend values into controlled React values.
 */
function toInputValue(value) {
  if (
    value === null ||
    value === undefined
  ) {
    return '';
  }

  return String(value);
}

/**
 * Convert one TreatmentResponse into frontend draft state.
 */
function normalizeTreatment(response) {
  return {
    ...response,

    draftTreatmentText:
      toInputValue(
        response?.treatmentText
      ),

    draftTotalTreatmentMinutes:
      toInputValue(
        response?.totalTreatmentMinutes
      ),
  };
}

/**
 * Capture the values that currently represent the server state.
 *
 * These values are used to determine which fields really changed.
 */
function createBaseline(treatment) {
  return {
    treatmentText:
      toInputValue(
        treatment?.treatmentText
      ),

    totalTreatmentMinutes:
      toInputValue(
        treatment?.totalTreatmentMinutes
      ),
  };
}

/**
 * Determine whether the current draft differs from the latest
 * backend state.
 */
function hasDraftChanges(
  treatment,
  baseline
) {
  if (!treatment) {
    return false;
  }

  return (
    treatment.draftTreatmentText !==
      baseline.treatmentText ||
    treatment.draftTotalTreatmentMinutes !==
      baseline.totalTreatmentMinutes
  );
}

/**
 * Validate fields that must be structurally valid before autosave.
 *
 * Required treatment completion remains part of final backend
 * clinical-note validation.
 */
function validateTreatment(
  treatment
) {
  const errors = {};

  if (
    treatment?.showTotalTreatmentTime &&
    treatment?.allowTreatmentTimeEdit
  ) {
    const value =
      treatment
        .draftTotalTreatmentMinutes;

    if (
      value !== null &&
      value !== undefined &&
      String(value).trim() !== ''
    ) {
      const numericValue =
        Number(value);

      if (
        !Number.isInteger(
          numericValue
        ) ||
        numericValue < 0
      ) {
        errors.totalTreatmentMinutes =
          'Total treatment minutes must be a non-negative whole number.';
      }
    }
  }

  return errors;
}

/**
 * Display one previous-treatment comparison block.
 */
function PreviousTreatment({
  treatment,
}) {
  const hasPrevious =
    Boolean(
      treatment.previousTreatmentText
    ) ||
    treatment.previousTotalTreatmentMinutes !==
      null &&
    treatment.previousTotalTreatmentMinutes !==
      undefined;

  return (
    <div
      style={{
        border:
          '1px solid #e0e3e7',
        borderRadius: 8,
        background:
          '#fafafa',
        padding: 11,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent:
            'space-between',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          marginBottom: 6,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform:
              'uppercase',
            color: '#777',
          }}
        >
          Previous Visit
        </div>

        {treatment.previousClinicalNoteId && (
          <div
            style={{
              fontSize: 11,
              color: '#777',
            }}
          >
            Note #
            {treatment.previousClinicalNoteId}
          </div>
        )}
      </div>

      {!hasPrevious && (
        <div
          style={{
            fontSize: 12,
            color: '#777',
          }}
        >
          No previous treatment
          documentation is available
          under this authorization.
        </div>
      )}

      {hasPrevious && (
        <div
          style={{
            display: 'grid',
            gap: 10,
          }}
        >
          {treatment.previousTreatmentText && (
            <div>
              <div
                style={{
                  marginBottom: 3,
                  fontSize: 11,
                  fontWeight: 700,
                  color: '#666',
                }}
              >
                Treatment
              </div>

              <div
                style={{
                  fontSize: 13,
                  lineHeight: 1.5,
                  whiteSpace:
                    'pre-wrap',
                }}
              >
                {
                  treatment.previousTreatmentText
                }
              </div>
            </div>
          )}

          {treatment.previousTotalTreatmentMinutes !==
            null &&
            treatment.previousTotalTreatmentMinutes !==
              undefined && (
            <div>
              <div
                style={{
                  marginBottom: 3,
                  fontSize: 11,
                  fontWeight: 700,
                  color: '#666',
                }}
              >
                Total Treatment Time
              </div>

              <div
                style={{
                  fontSize: 13,
                }}
              >
                {
                  treatment.previousTotalTreatmentMinutes
                }{' '}
                minutes
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Clinical-note Treatment editor.
 *
 * Features:
 *
 * - insurance-rule-controlled configuration
 * - backend-selected treatment template
 * - unsaved treatment preview
 * - explicit first-save confirmation
 * - optional patient-specific wording
 * - payer-controlled treatment-time editing
 * - previous-visit comparison
 * - copy-forward information
 * - 900ms autosave
 * - optimistic-lock version refresh
 */
export default function ClinicalNoteTreatmentSection({
  clinicalNoteId,
  disabled = false,
  onSaved,
  onVersionChange,
  navigationCoordinator,
}) {
  const [
    treatment,
    setTreatment,
  ] = useState(null);

  // Presentation only. Collapsing never unmounts the editor or changes its draft.
  const [editorExpanded, setEditorExpanded] = useState(false);
  const [editorLarge, setEditorLarge] = useState(false);
  const [previousExpanded, setPreviousExpanded] = useState(false);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    loadError,
    setLoadError,
  ] = useState('');

  const [
    saveError,
    setSaveError,
  ] = useState('');

  const [
    saveStatus,
    setSaveStatus,
  ] = useState('saved');

  const [
    editTick,
    setEditTick,
  ] = useState(0);

  const [
    initialized,
    setInitialized,
  ] = useState(false);

  /*
   * Mutable autosave state.
   */
  const treatmentRef =
    useRef(null);

  const baselineRef =
    useRef({
      treatmentText: '',
      totalTreatmentMinutes: '',
    });

  const versionRef =
    useRef(null);

  const revisionRef =
    useRef(0);

  const dirtyRef =
    useRef(false);

  const savingRef =
    useRef(false);

  const failedRef =
    useRef(false);

  const mountedRef =
    useRef(true);

  const flushSaveRef =
    useRef(null);

  // Navigation requires a real awaitable request, not only savingRef.
  const inFlightSaveRef = useRef(null);
  const initializedRef = useRef(false);
  const loadErrorRef = useRef('');
  const saveErrorRef = useRef('');


  useEffect(() => {
    treatmentRef.current =
      treatment;
  }, [treatment]);

  useEffect(() => {
    return () => {
      mountedRef.current =
        false;
    };
  }, []);

  /**
   * Load:
   *
   * - treatment record or backend-generated unsaved preview
   * - current ClinicalNoteMain optimistic-lock version
   */
  useEffect(() => {
    let cancelled = false;

    async function loadTreatment() {
      setLoading(true);
      setLoadError('');
      loadErrorRef.current = '';
      setSaveError('');
      saveErrorRef.current = '';
      setInitialized(false);
      initializedRef.current = false;

      dirtyRef.current =
        false;

      failedRef.current =
        false;

      savingRef.current =
        false;

      revisionRef.current =
        0;

      try {
        const [
          treatmentResponse,
          noteResponse,
        ] =
          await Promise.all([
            clinicalNoteService
              .getTreatment(
                clinicalNoteId
              ),

            clinicalNoteService
              .getClinicalNote(
                clinicalNoteId
              ),
          ]);

        if (cancelled) {
          return;
        }

        const version =
          resolveEntityVersion(
            noteResponse
          );

        if (
          version === null ||
          version === undefined
        ) {
          throw new Error(
            'The clinical note version could not be loaded.'
          );
        }

        if (
          !treatmentResponse ||
          typeof treatmentResponse !==
            'object'
        ) {
          throw new Error(
            'Treatment configuration could not be loaded.'
          );
        }

        const normalized =
          normalizeTreatment(
            treatmentResponse
          );

        versionRef.current =
          version;

        treatmentRef.current =
          normalized;

        baselineRef.current =
          createBaseline(
            treatmentResponse
          );

        // A new preview should be easy to review before explicit confirmation.
        // Existing treatment documentation starts condensed to reduce scrolling.
        setEditorExpanded(!normalized.clinicalNoteTreatmentId);
        setEditorLarge(false);
        setPreviousExpanded(false);

        setTreatment(
          normalized
        );

        setSaveStatus(
          normalized.clinicalNoteTreatmentId
            ? 'saved'
            : 'unconfirmed'
        );

        initializedRef.current = true;
        setInitialized(true);
      } catch (error) {
        if (cancelled) {
          return;
        }

        loadErrorRef.current = error?.message ||
          'Treatment information could not be loaded.';
        setLoadError(loadErrorRef.current);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    if (clinicalNoteId) {
      loadTreatment();
    } else {
      loadErrorRef.current = 'Clinical note ID is required.';
      setLoadError(loadErrorRef.current);

      setLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [clinicalNoteId]);

  /**
   * Refresh the parent ClinicalNoteMain version after treatment save.
   */
  const refreshEntityVersion =
    useCallback(
      async () => {
        const noteResponse =
          await clinicalNoteService
            .getClinicalNote(
              clinicalNoteId
            );

        const newVersion =
          resolveEntityVersion(
            noteResponse
          );

        if (
          newVersion === null ||
          newVersion === undefined
        ) {
          throw new Error(
            'Treatment was saved, but the updated clinical note version could not be loaded.'
          );
        }

        versionRef.current =
          newVersion;

        if (
          typeof onVersionChange ===
          'function'
        ) {
          onVersionChange(
            newVersion,
            noteResponse
          );
        }

        return noteResponse;
      },
      [
        clinicalNoteId,
        onVersionChange,
      ]
    );

  /**
   * Recalculate whether the current local draft differs from the
   * latest successful backend state.
   */
  function recalculateDirtyState(
    nextTreatment
  ) {
    const changed =
      hasDraftChanges(
        nextTreatment,
        baselineRef.current
      );

    dirtyRef.current =
      changed;

    if (changed) {
      setSaveStatus(
        'unsaved'
      );
    } else if (
      nextTreatment
        ?.clinicalNoteTreatmentId
    ) {
      setSaveStatus(
        'saved'
      );
    } else {
      setSaveStatus(
        'unconfirmed'
      );
    }

    return changed;
  }

  /**
   * Update treatment wording.
   */
  function updateTreatmentText(
    value
  ) {
    if (
      disabled ||
      !treatmentRef.current
        ?.allowPatientSpecificEdit
    ) {
      return;
    }

    const nextTreatment = {
      ...treatmentRef.current,

      draftTreatmentText:
        value,
    };

    treatmentRef.current =
      nextTreatment;

    setTreatment(
      nextTreatment
    );

    revisionRef.current +=
      1;

    failedRef.current =
      false;

    saveErrorRef.current = '';
    setSaveError('');

    recalculateDirtyState(
      nextTreatment
    );

    setEditTick(
      (current) =>
        current + 1
    );
  }

  /**
   * Update editable treatment minutes.
   */
  function updateTreatmentMinutes(
    value
  ) {
    if (
      disabled ||
      !treatmentRef.current
        ?.allowTreatmentTimeEdit
    ) {
      return;
    }

    const nextTreatment = {
      ...treatmentRef.current,

      draftTotalTreatmentMinutes:
        value,
    };

    treatmentRef.current =
      nextTreatment;

    setTreatment(
      nextTreatment
    );

    revisionRef.current +=
      1;

    failedRef.current =
      false;

    saveErrorRef.current = '';
    setSaveError('');

    recalculateDirtyState(
      nextTreatment
    );

    setEditTick(
      (current) =>
        current + 1
    );
  }

  /**
   * Build a save request containing only values that were actually
   * changed by the clinician.
   *
   * This matters especially on the first save:
   *
   * - treatmentText = null allows the backend to safely perform its
   *   own template/copy-forward resolution.
   *
   * - totalTreatmentMinutes = null allows the backend to apply its
   *   payer/template default.
   *
   * This avoids falsely converting a backend-generated preview into
   * an arbitrary frontend modification.
   */
  function buildSaveRequest(
    currentTreatment
  ) {
    const baseline =
      baselineRef.current;

    const treatmentTextChanged =
      currentTreatment
        .draftTreatmentText !==
      baseline.treatmentText;

    const treatmentMinutesChanged =
      currentTreatment
        .draftTotalTreatmentMinutes !==
      baseline.totalTreatmentMinutes;

    return {
      expectedVersion:
        versionRef.current,

      treatmentTemplateId:
        currentTreatment
          .treatmentTemplateId ??
        null,

      treatmentText:
        treatmentTextChanged
          ? currentTreatment
              .draftTreatmentText
          : null,

      totalTreatmentMinutes:
        treatmentMinutesChanged
          ? currentTreatment
              .draftTotalTreatmentMinutes
          : null,
    };
  }

  /**
   * Apply a successful treatment save.
   *
   * If the clinician changed another field while the request was in
   * flight, preserve that newer local draft.
   */
  function applySuccessfulSave(
    response,
    revisionBeingSaved
  ) {
    const normalizedResponse =
      normalizeTreatment(
        response
      );

    const newBaseline =
      createBaseline(
        response
      );

    baselineRef.current =
      newBaseline;

    const currentRevision =
      revisionRef.current;

    if (
      currentRevision ===
      revisionBeingSaved
    ) {
      treatmentRef.current =
        normalizedResponse;

      setTreatment(
        normalizedResponse
      );

      dirtyRef.current =
        false;

      setSaveStatus(
        'saved'
      );

      return;
    }

    /*
     * Newer edits occurred during the save.
     *
     * Refresh server-derived metadata but preserve current draft
     * values.
     */
    const currentDraft =
      treatmentRef.current;

    const merged = {
      ...normalizedResponse,

      draftTreatmentText:
        currentDraft
          ?.draftTreatmentText ??
        normalizedResponse
          .draftTreatmentText,

      draftTotalTreatmentMinutes:
        currentDraft
          ?.draftTotalTreatmentMinutes ??
        normalizedResponse
          .draftTotalTreatmentMinutes,
    };

    treatmentRef.current =
      merged;

    setTreatment(
      merged
    );

    const stillDirty =
      hasDraftChanges(
        merged,
        newBaseline
      );

    dirtyRef.current =
      stillDirty;

    setSaveStatus(
      stillDirty
        ? 'unsaved'
        : 'saved'
    );
  }

  /**
   * Save the treatment section.
   *
   * forceSave is used by "Confirm & Save Treatment" so a backend
   * preview can be persisted even when the clinician has not changed
   * any fields.
   */
  const saveTreatment =
    useCallback(
      async (
        forceSave = false
      ) => {
        if (
          disabled ||
          !initialized ||
          savingRef.current ||
          !treatmentRef.current
        ) {
          return;
        }

        const currentTreatment =
          treatmentRef.current;

        const validationErrors =
          validateTreatment(
            currentTreatment
          );

        if (
          Object.keys(
            validationErrors
          ).length > 0
        ) {
          setSaveStatus(
            'invalid'
          );

          return;
        }

        if (
          !forceSave &&
          !dirtyRef.current
        ) {
          return;
        }

        savingRef.current =
          true;

        failedRef.current =
          false;

        setSaveStatus(
          'saving'
        );

        saveErrorRef.current = '';
        setSaveError('');

        const revisionBeingSaved =
          revisionRef.current;

        try {
          if (
            versionRef.current ===
              null ||
            versionRef.current ===
              undefined
          ) {
            await refreshEntityVersion();
          }

          const response =
            await clinicalNoteService
              .saveTreatment(
                clinicalNoteId,
                buildSaveRequest(
                  currentTreatment
                )
              );

          if (
            !mountedRef.current
          ) {
            return;
          }

          /*
           * Every child save updates the parent note's optimistic
           * version.
           */
          const noteResponse =
            await refreshEntityVersion();

          if (
            !mountedRef.current
          ) {
            return;
          }

          applySuccessfulSave(
            response,
            revisionBeingSaved
          );

          if (
            typeof onSaved ===
            'function'
          ) {
            onSaved(
              response,
              noteResponse
            );
          }
        } catch (error) {
          if (
            !mountedRef.current
          ) {
            return;
          }

          failedRef.current =
            true;

          setSaveStatus(
            'error'
          );

          saveErrorRef.current = error?.message ||
            'Treatment information could not be autosaved.';
          setSaveError(saveErrorRef.current);
        } finally {
          savingRef.current =
            false;

          /*
           * If a newer edit occurred while the request was active,
           * schedule another save using the refreshed parent version.
           */
          if (
            mountedRef.current &&
            dirtyRef.current &&
            !failedRef.current
          ) {
            window.setTimeout(
              () => {
                flushSaveRef
                  .current?.(
                    false
                  );
              },
              150
            );
          }
        }
      },
      [
        clinicalNoteId,
        disabled,
        initialized,
        onSaved,
        refreshEntityVersion,
      ]
    );

  /**
   * Wrap the existing autosave in an awaitable promise. Subsequent callers
   * share the same request rather than treating an active save as complete.
   * The existing 900ms debounce and explicit confirmation remain unchanged.
   */
  useEffect(() => {
    flushSaveRef.current = (forceSave = false) => {
      if (inFlightSaveRef.current) return inFlightSaveRef.current;
      const task = Promise.resolve().then(() => saveTreatment(forceSave));
      const tracked = task.finally(() => {
        if (inFlightSaveRef.current === tracked) {
          inFlightSaveRef.current = null;
        }
      });
      inFlightSaveRef.current = tracked;
      return tracked;
    };
  }, [saveTreatment]);

  /**
   * The backend's untouched treatment preview is intentionally not dirty.
   * A user edit must be saved; a failed/invalid edit blocks navigation.
   * Unlike Confirm & Save Treatment, navigation never force-saves a preview.
   */
  useEffect(() => {
    if (!navigationCoordinator || !clinicalNoteId) return undefined;
    return navigationCoordinator.register(clinicalNoteId, 'treatment', {
      flush: async () => {
        // Wait for any save already initiated by the debounce or button.
        if (inFlightSaveRef.current) await inFlightSaveRef.current;
        // An edit could have occurred while the earlier save was running.
        // Flush again until all changed values are persisted or an error occurs.
        let attempts = 0;
        while (dirtyRef.current && !failedRef.current &&
          attempts < 20 && initializedRef.current) {
          const before = revisionRef.current;
          await flushSaveRef.current?.(false);
          if (inFlightSaveRef.current) await inFlightSaveRef.current;
          attempts += 1;
          if (dirtyRef.current && revisionRef.current === before &&
            !inFlightSaveRef.current) break;
        }
      },
      getState: () => ({
        dirty: !initializedRef.current || dirtyRef.current,
        saving: Boolean(inFlightSaveRef.current),
        invalid: Boolean(treatmentRef.current && dirtyRef.current &&
          Object.keys(validateTreatment(treatmentRef.current)).length),
        error: loadErrorRef.current ||
          (!initializedRef.current ? 'Treatment is still loading.' : '') ||
          (failedRef.current
            ? (saveErrorRef.current ||
              'Treatment could not be saved. Use Retry Save before switching visits.')
            : ''),
      }),
    });
  }, [navigationCoordinator, clinicalNoteId]);

  /**
   * Standard 900ms autosave after treatment edits.
   */
  useEffect(() => {
    if (
      !initialized ||
      disabled ||
      !dirtyRef.current ||
      failedRef.current
    ) {
      return undefined;
    }

    const timer =
      window.setTimeout(
        () => {
          flushSaveRef
            .current?.(
              false
            );
        },
        AUTOSAVE_DELAY_MS
      );

    return () => {
      window.clearTimeout(
        timer
      );
    };
  }, [
    editTick,
    initialized,
    disabled,
  ]);

  /**
   * Retry a failed save without requiring another edit.
   */
  function retrySave() {
    if (disabled) {
      return;
    }

    failedRef.current =
      false;

    saveErrorRef.current = '';
    setSaveError('');

    flushSaveRef.current?.(
      !treatmentRef.current
        ?.clinicalNoteTreatmentId
    );
  }

  const validationErrors =
    useMemo(
      () =>
        treatment
          ? validateTreatment(
              treatment
            )
          : {},
      [treatment]
    );

  const isUnsavedPreview =
    Boolean(
      treatment &&
      !treatment
        .clinicalNoteTreatmentId
    );

  const textEditable =
    Boolean(
      treatment &&
      treatment
        .allowPatientSpecificEdit &&
      !disabled
    );

  const treatmentTimeEditable =
    Boolean(
      treatment &&
      treatment
        .showTotalTreatmentTime &&
      treatment
        .allowTreatmentTimeEdit &&
      !disabled
    );

  const previousAvailable = Boolean(
    treatment?.previousTreatmentText ||
    treatment?.previousTotalTreatmentMinutes !== null &&
    treatment?.previousTotalTreatmentMinutes !== undefined
  );

  const treatmentTextChanged =
    Boolean(
      treatment &&
      treatment
        .draftTreatmentText !==
        baselineRef.current
          .treatmentText
    );

  let saveStatusText =
    disabled
      ? 'Read only'
      : 'Saved';

  switch (saveStatus) {
    case 'unconfirmed':
      saveStatusText =
        'Not yet confirmed';
      break;

    case 'unsaved':
      saveStatusText =
        'Unsaved changes';
      break;

    case 'saving':
      saveStatusText =
        'Saving…';
      break;

    case 'invalid':
      saveStatusText =
        'Needs correction';
      break;

    case 'error':
      saveStatusText =
        'Autosave failed';
      break;

    default:
      break;
  }

  if (loading) {
    return (
      <section
        style={{
          border:
            '1px solid #ddd',
          borderRadius: 10,
          padding: 16,
          background: '#fff',
        }}
      >
        Loading treatment…
      </section>
    );
  }

  if (loadError) {
    return (
      <section
        style={{
          border:
            '1px solid #c62828',
          borderRadius: 10,
          padding: 16,
          background:
            '#fff5f5',
        }}
      >
        <strong>
          Treatment section could not
          be loaded
        </strong>

        <p
          style={{
            marginBottom: 0,
          }}
        >
          {loadError}
        </p>
      </section>
    );
  }

  if (!treatment) {
    return null;
  }

  return (
    <section
      style={{
        border:
          '1px solid #d7d7d7',
        borderRadius: 10,
        background: '#fff',
        overflow: 'hidden',
      }}
    >
      {/* =====================================================
          SECTION HEADER
          ===================================================== */}
      <div
        style={{
          display: 'flex',
          justifyContent:
            'space-between',
          alignItems:
            'flex-start',
          gap: 12,
          flexWrap: 'wrap',
          padding:
            '13px 16px',
          background:
            '#f7f8fa',
          borderBottom:
            '1px solid #ddd',
        }}
      >
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              flexWrap: 'wrap',
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: 17,
              }}
            >
              {treatment.displayNameSnapshot ||
                'Treatment'}
            </h3>

            {isUnsavedPreview && (
              <span
                style={{
                  borderRadius: 999,
                  padding:
                    '2px 7px',
                  background:
                    '#fff3cd',
                  color: '#795500',
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                Preview
              </span>
            )}

            {treatment.copiedForward && (
              <span
                style={{
                  borderRadius: 999,
                  padding:
                    '2px 7px',
                  background:
                    '#e8f4fd',
                  color: '#205493',
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                Copied forward
              </span>
            )}

            {treatment.patientSpecificModified && (
              <span
                style={{
                  borderRadius: 999,
                  padding:
                    '2px 7px',
                  background:
                    '#fff3cd',
                  color: '#795500',
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                Patient-specific wording
              </span>
            )}

            {treatmentTextChanged &&
              treatment.allowPatientSpecificEdit && (
                <span
                  style={{
                    borderRadius: 999,
                    padding:
                      '2px 7px',
                    background:
                      '#eef3f8',
                    color: '#334e68',
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  Wording changed
                </span>
              )}
          </div>

          <div
            style={{
              marginTop: 5,
              display: 'flex',
              gap: 10,
              flexWrap: 'wrap',
              fontSize: 12,
              color: '#666',
            }}
          >
            {treatment.treatmentCodeSnapshot && (
              <span>
                {
                  treatment.treatmentCodeSnapshot
                }
              </span>
            )}

            {treatment.insuranceRuleDisplayName && (
              <span>
                Insurance Rule:{' '}
                {
                  treatment.insuranceRuleDisplayName
                }
              </span>
            )}

            {!treatment.insuranceRuleDisplayName &&
              treatment.insuranceRuleCode && (
                <span>
                  Insurance Rule:{' '}
                  {
                    treatment.insuranceRuleCode
                  }
                </span>
              )}
          </div>
        </div>

        <div
          aria-live="polite"
          style={{
            fontSize: 13,
            fontWeight: 700,
            color:
              saveStatus ===
              'error'
                ? '#b42318'
                : saveStatus ===
                    'invalid'
                  ? '#9a6700'
                  : saveStatus ===
                      'saving'
                    ? '#555'
                    : saveStatus ===
                        'unconfirmed'
                      ? '#9a6700'
                      : '#2e6a35',
          }}
        >
          {saveStatusText}
        </div>
      </div>

      <div
        style={{
          padding: 16,
        }}
      >
        {/* ===================================================
            UNSAVED PREVIEW INFORMATION
            =================================================== */}
        {isUnsavedPreview && (
          <div
            style={{
              marginBottom: 10,
              border:
                '1px solid #d6a100',
              borderRadius: 8,
              background:
                '#fff8df',
              padding: 11,
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            The treatment configuration
            has been selected by the
            backend but has not yet been
            saved to this clinical note.

            {' '}

            Confirm the treatment below
            to create the treatment
            record for this visit.
          </div>
        )}

        {/* The editor stays mounted while minimized, protecting unsaved text. */}
        <div className="clinical-treatment-compact__toolbar">
          <button
            type="button"
            className="clinical-treatment-compact__button clinical-treatment-compact__button--primary"
            onClick={() => setEditorExpanded((value) => !value)}
            aria-expanded={editorExpanded}
            aria-controls="clinical-treatment-editor-panel"
          >
            {editorExpanded ? 'Minimize Treatment' : 'Edit / View Treatment'}
          </button>
          {editorExpanded && (
            <button
              type="button"
              className="clinical-treatment-compact__button"
              onClick={() => setEditorLarge((value) => !value)}
              aria-pressed={editorLarge}
            >
              {editorLarge ? 'Normal Size' : 'Expand Editor'}
            </button>
          )}
          <button
            type="button"
            className="clinical-treatment-compact__button"
            onClick={() => setPreviousExpanded((value) => !value)}
            aria-expanded={previousExpanded}
            aria-controls="clinical-treatment-previous-panel"
          >
            {previousExpanded ? 'Hide Previous Visit' : previousAvailable ? 'Previous Visit' : 'Previous Visit (none)'}
          </button>
        </div>

        {!editorExpanded && (
          <div className="clinical-treatment-compact__summary" aria-label="Current treatment preview">
            {treatment.draftTreatmentText?.trim()
              ? treatment.draftTreatmentText.trim().slice(0, 240) +
                (treatment.draftTreatmentText.trim().length > 240 ? '…' : '')
              : 'No treatment wording is currently available.'}
          </div>
        )}

        {/* ===================================================
            TREATMENT WORDING
            =================================================== */}
        <div
          id="clinical-treatment-editor-panel"
          className="clinical-treatment-compact__editor"
          style={{ display: editorExpanded ? 'block' : 'none' }}
          aria-hidden={!editorExpanded}
        >
          <label
            htmlFor="clinical-treatment-text"
            style={{
              display: 'block',
              marginBottom: 5,
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            Treatment Documentation
            <span
              aria-hidden="true"
              style={{
                marginLeft: 4,
                color: '#b42318',
              }}
            >
              *
            </span>
          </label>

          <textarea
            id="clinical-treatment-text"
            className={editorLarge ? 'clinical-treatment-compact__textarea--large' : ''}
            rows={editorLarge ? 14 : 5}
            value={
              treatment
                .draftTreatmentText
            }
            disabled={
              !textEditable
            }
            onChange={(event) =>
              updateTreatmentText(
                event.target.value
              )
            }
            style={{
              width: '100%',
              boxSizing:
                'border-box',
              resize:
                textEditable
                  ? 'vertical'
                  : 'none',
              minHeight: editorLarge ? 340 : 110,
              border:
                '1px solid #bbb',
              borderRadius: 6,
              padding: 10,
              fontSize: 14,
              lineHeight: 1.5,
              background:
                textEditable
                  ? '#fff'
                  : '#f3f4f6',
            }}
          />

          <div
            style={{
              marginTop: 5,
              fontSize: 11,
              color: '#666',
            }}
          >
            {disabled
              ? 'Treatment documentation is read only.'
              : treatment.allowPatientSpecificEdit
                ? 'Patient-specific treatment wording may be edited for this template.'
                : 'Treatment wording is controlled by the configured treatment template and cannot be manually changed.'}
          </div>
        </div>

        {/* ===================================================
            TREATMENT TIME
            =================================================== */}
        {treatment.showTotalTreatmentTime && (
          <div
            style={{
              marginTop: 15,
              maxWidth: 360,
            }}
          >
            <label
              htmlFor="clinical-treatment-minutes"
              style={{
                display: 'block',
                marginBottom: 5,
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              Total Treatment Time

              {treatment.totalTreatmentTimeRequired && (
                <span
                  aria-hidden="true"
                  style={{
                    marginLeft: 4,
                    color: '#b42318',
                  }}
                >
                  *
                </span>
              )}
            </label>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <input
                id="clinical-treatment-minutes"
                type="number"
                min="0"
                step="1"
                inputMode="numeric"
                value={
                  treatment
                    .draftTotalTreatmentMinutes
                }
                disabled={
                  !treatmentTimeEditable
                }
                onChange={(event) =>
                  updateTreatmentMinutes(
                    event.target.value
                  )
                }
                aria-invalid={
                  Boolean(
                    validationErrors
                      .totalTreatmentMinutes
                  )
                }
                style={{
                  width: 130,
                  boxSizing:
                    'border-box',
                  minHeight: 38,
                  border:
                    validationErrors
                      .totalTreatmentMinutes
                      ? '1px solid #b42318'
                      : '1px solid #bbb',
                  borderRadius: 6,
                  padding:
                    '7px 9px',
                  fontSize: 14,
                  background:
                    treatmentTimeEditable
                      ? '#fff'
                      : '#f3f4f6',
                }}
              />

              <span
                style={{
                  fontSize: 13,
                  color: '#555',
                }}
              >
                minutes
              </span>
            </div>

            {validationErrors
              .totalTreatmentMinutes && (
              <div
                role="alert"
                style={{
                  marginTop: 5,
                  fontSize: 12,
                  color: '#b42318',
                }}
              >
                {
                  validationErrors
                    .totalTreatmentMinutes
                }
              </div>
            )}

            {!disabled &&
              !treatment.allowTreatmentTimeEdit && (
                <div
                  style={{
                    marginTop: 5,
                    fontSize: 11,
                    color: '#666',
                  }}
                >
                  Treatment time is
                  controlled by the
                  applicable insurance
                  rule and cannot be
                  manually changed.
                </div>
              )}

            {!disabled &&
              treatment.allowTreatmentTimeEdit && (
                <div
                  style={{
                    marginTop: 5,
                    fontSize: 11,
                    color: '#666',
                  }}
                >
                  Treatment time may be
                  adjusted for this
                  insurance rule.
                </div>
              )}
          </div>
        )}

        {/* ===================================================
            COPY-FORWARD INFORMATION
            =================================================== */}
        {treatment.copiedForward && (
          <div
            style={{
              marginTop: 10,
              border:
                '1px solid #c9def2',
              borderRadius: 8,
              background:
                '#f4f9fd',
              padding: 11,
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            Treatment wording was safely
            copied forward from a
            compatible prior clinical
            note.

            {treatment.copiedFromClinicalNoteId && (
              <>
                {' '}
                Source note #
                {
                  treatment.copiedFromClinicalNoteId
                }.
              </>
            )}
          </div>
        )}

        {/* ===================================================
            PREVIOUS VISIT COMPARISON
            =================================================== */}
        <div
          id="clinical-treatment-previous-panel"
          style={{
            marginTop: 10,
            display: previousExpanded ? 'block' : 'none',
          }}
          aria-hidden={!previousExpanded}
        >
          <PreviousTreatment
            treatment={
              treatment
            }
          />
        </div>

        {/* ===================================================
            FIRST SAVE / CONFIRM
            =================================================== */}
        {isUnsavedPreview &&
          !disabled && (
          <div
            style={{
              marginTop: 15,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
            }}
          >
            <button
              type="button"
              disabled={
                savingRef.current ||
                Object.keys(
                  validationErrors
                ).length > 0
              }
              onClick={() =>
                flushSaveRef
                  .current?.(
                    true
                  )
              }
            >
              {saveStatus ===
              'saving'
                ? 'Saving…'
                : 'Confirm & Save Treatment'}
            </button>

            <span
              style={{
                fontSize: 12,
                color: '#666',
              }}
            >
              This confirms the
              backend-selected treatment
              configuration for this
              visit.
            </span>
          </div>
        )}

        {/* ===================================================
            SAVE ERROR
            =================================================== */}
        {saveError && (
          <div
            role="alert"
            style={{
              marginTop: 15,
              border:
                '1px solid #b42318',
              borderRadius: 8,
              background:
                '#fff5f5',
              padding: 11,
            }}
          >
            <strong>
              Treatment was not saved.
            </strong>

            <div
              style={{
                marginTop: 4,
                fontSize: 13,
              }}
            >
              {saveError}
            </div>

            {!disabled && (
              <button
                type="button"
                onClick={
                  retrySave
                }
                style={{
                  marginTop: 8,
                }}
              >
                Retry Save
              </button>
            )}
          </div>
        )}

        {disabled && (
          <div
            style={{
              marginTop: 14,
              fontSize: 13,
              color: '#666',
            }}
          >
            Treatment documentation is
            read only because this
            clinical note is no longer
            in the normal Draft editing
            state.
          </div>
        )}
      </div>
    </section>
  );
}